import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Typography,
  Switch,
  DatePicker,
  Space,
  Segmented,
  Button,
  Tabs,
  Input,
  Select,
} from 'antd';
import {
  SyncOutlined,
  DashboardOutlined,
  DesktopOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import ManufacturingDashboardAlerts from '@/components/manufacturing/dashboard/ManufacturingDashboardAlerts';
import ExecutiveHealthRibbon from '@/components/manufacturing/control-tower/ExecutiveHealthRibbon';
import ProductionExecutionMonitor from '@/components/manufacturing/control-tower/ProductionExecutionMonitor';
import FabricationUnitControlBoard from '@/components/manufacturing/control-tower/FabricationUnitControlBoard';
import InspectionControlCenter from '@/components/manufacturing/control-tower/InspectionControlCenter';
import OutsourcedOperationsTower from '@/components/manufacturing/control-tower/OutsourcedOperationsTower';
import DeliveryReadinessCenter from '@/components/manufacturing/control-tower/DeliveryReadinessCenter';
import TraceabilityCenter from '@/components/manufacturing/control-tower/TraceabilityCenter';
import MaterialReadinessCenter from '@/components/manufacturing/control-tower/MaterialReadinessCenter';
import ProductionRequestCenter from '@/components/manufacturing/control-tower/ProductionRequestCenter';
import RouteSheetControl from '@/components/manufacturing/control-tower/RouteSheetControl';
import DocumentCollectionStatus from '@/components/manufacturing/control-tower/DocumentCollectionStatus';
import BottleneckAnalyzer from '@/components/manufacturing/control-tower/BottleneckAnalyzer';
import ProductionTimeline from '@/components/manufacturing/control-tower/ProductionTimeline';
import SmartAlertsPanel from '@/components/manufacturing/control-tower/SmartAlertsPanel';
import ManufacturingAnalytics from '@/components/manufacturing/control-tower/ManufacturingAnalytics';
import AiBriefStrip from '@/components/manufacturing/control-tower/AiBriefStrip';
import AttentionQueueZone from '@/components/manufacturing/control-tower/AttentionQueueZone';
import {
  getMfgControlTowerLayout,
  getMfgRefreshIntervalMs,
} from '@/components/manufacturing/control-tower/layoutProfile';
import type { ControlTowerQueryParams } from '@/services/manufacturingControlTower.service';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';

const { Title, Text } = Typography;

const ManufacturingControlTowerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const layoutUser = user as Parameters<typeof getMfgControlTowerLayout>[0];
  const { profile, visibility } = getMfgControlTowerLayout(layoutUser);
  const prefRefresh = getMfgRefreshIntervalMs(layoutUser);

  const tvMode = searchParams.get('mode') === 'tv' || searchParams.get('mode') === 'TV';
  const towerTab = searchParams.get('tab') === 'analytics' ? 'analytics' : 'operations';
  const [autoRefresh, setAutoRefresh] = useState(Boolean(prefRefresh));
  const [kpiPeriod, setKPIPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [timelinePeriod, setTimelinePeriod] = useState<'today' | 'week' | 'month'>('week');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [executionScope, setExecutionScope] = useState<string | undefined>(
    searchParams.get('execution_scope') || undefined
  );
  const [workOrderStatus, setWorkOrderStatus] = useState<string | undefined>(
    searchParams.get('work_order_status') || undefined
  );
  const dateFromParam = searchParams.get('date_from');
  const dateToParam = searchParams.get('date_to');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    dateFromParam ? dayjs(dateFromParam) : null,
    dateToParam ? dayjs(dateToParam) : null,
  ]);

  const refreshMs = autoRefresh ? prefRefresh || 30_000 : undefined;

  const filters: ControlTowerQueryParams = useMemo(() => {
    const f: ControlTowerQueryParams = { period: kpiPeriod };
    if (search.trim()) f.search = search.trim();
    if (executionScope) f.execution_scope = executionScope;
    if (workOrderStatus) f.work_order_status = workOrderStatus;
    if (dateRange[0]) f.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]) f.date_to = dateRange[1].format('YYYY-MM-DD');
    if (profile === 'supervisor' && user?.id) {
      f.supervisor_user_id = String(user.id);
    }
    return f;
  }, [kpiPeriod, search, executionScope, workOrderStatus, dateRange, profile, user?.id]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => setLastUpdated(new Date()), refreshMs || 30_000);
    return () => clearInterval(t);
  }, [autoRefresh, refreshMs]);

  const syncUrlFilters = (next: {
    search?: string;
    execution_scope?: string;
    work_order_status?: string;
    date_from?: string | null;
    date_to?: string | null;
    tab?: string;
  }) => {
    const params = new URLSearchParams(searchParams);
    const setOrDel = (key: string, val?: string | null) => {
      if (val) params.set(key, val);
      else params.delete(key);
    };
    if ('search' in next) setOrDel('search', next.search || null);
    if ('execution_scope' in next) setOrDel('execution_scope', next.execution_scope || null);
    if ('work_order_status' in next) setOrDel('work_order_status', next.work_order_status || null);
    if ('date_from' in next) setOrDel('date_from', next.date_from || null);
    if ('date_to' in next) setOrDel('date_to', next.date_to || null);
    if ('tab' in next && next.tab) params.set('tab', next.tab);
    setSearchParams(params, { replace: true });
  };

  const toggleTv = () => {
    const next = new URLSearchParams(searchParams);
    if (tvMode) next.delete('mode');
    else next.set('mode', 'tv');
    setSearchParams(next);
  };

  const setTowerTab = (tab: 'operations' | 'analytics') => {
    syncUrlFilters({ tab });
  };

  const pageClass = useMemo(() => (tvMode ? 'control-tower-tv' : ''), [tvMode]);

  const opsContent = (
    <>
      {visibility.aiBrief && (
        <AiBriefStrip profile={profile} filters={filters} refreshInterval={refreshMs} />
      )}
      {visibility.myQueue && (
        <AttentionQueueZone filters={filters} refreshInterval={refreshMs} />
      )}

      <div style={{ marginBottom: 16 }}>
        <ExecutiveHealthRibbon
          period={kpiPeriod}
          filters={filters}
          refreshInterval={refreshMs}
          tvMode={tvMode}
        />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          {visibility.execution && (
            <ControlTowerSectionCard zone="ordersInProgress" sectionId="execution" title="Execution">
              <ProductionExecutionMonitor filters={filters} />
            </ControlTowerSectionCard>
          )}
          {visibility.fabrication && (
            <div id="fabrication">
              <FabricationUnitControlBoard filters={filters} />
            </div>
          )}
          {visibility.inspections && (
            <div id="inspections">
              <InspectionControlCenter filters={filters} />
            </div>
          )}
          {visibility.fulfilment && (
            <ControlTowerSectionCard
              zone="deliveries"
              sectionId="fulfilment"
              title="Fulfilment"
              style={{ marginTop: 16 }}
            >
              {visibility.delivery && <DeliveryReadinessCenter filters={filters} />}
              {visibility.outsourced && <OutsourcedOperationsTower filters={filters} />}
              {visibility.material && <MaterialReadinessCenter filters={filters} />}
            </ControlTowerSectionCard>
          )}
          {visibility.traceDocs && (
            <ControlTowerSectionCard
              zone="documentPrep"
              sectionId="trace-docs"
              title="Trace & documents"
              style={{ marginTop: 16 }}
            >
              <TraceabilityCenter filters={filters} />
              {visibility.documents && <DocumentCollectionStatus />}
              {visibility.routeSheets && <RouteSheetControl filters={filters} />}
            </ControlTowerSectionCard>
          )}
          {visibility.plant && (
            <ControlTowerSectionCard
              zone="neutral"
              sectionId="plant"
              title="Plant"
              style={{ marginTop: 16 }}
            >
              {visibility.bottlenecks && <BottleneckAnalyzer filters={filters} />}
              {visibility.timeline && (
                <ProductionTimeline
                  period={timelinePeriod}
                  onPeriodChange={setTimelinePeriod}
                  filters={filters}
                />
              )}
              {visibility.productionRequests && <ProductionRequestCenter />}
            </ControlTowerSectionCard>
          )}
        </Col>
        <Col xs={24} xl={8}>
          {visibility.alerts && <SmartAlertsPanel filters={filters} refreshInterval={refreshMs} />}
        </Col>
      </Row>
    </>
  );

  return (
    <div
      className={pageClass}
      style={{
        padding: tvMode ? 16 : 24,
        background: '#f0f2f5',
        minHeight: '100vh',
        fontSize: tvMode ? 16 : undefined,
      }}
    >
      <style>{`
        .control-tower-tv .ant-statistic-title { font-size: 14px; }
        .control-tower-tv .ant-statistic-content { font-size: 28px; }
        .control-tower-tv .ant-card-head-title { font-size: 18px; }
      `}</style>

      <ManufacturingDashboardAlerts
        enabled={autoRefresh}
        soundEnabled={!tvMode}
        refreshInterval={refreshMs}
      />

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="center">
            <DashboardOutlined style={{ fontSize: tvMode ? 40 : 32, color: '#145A54' }} />
            <div>
              <Title level={tvMode ? 1 : 2} style={{ margin: 0 }}>
                Manufacturing Control Tower
              </Title>
              <Text type="secondary">
                {profile === 'supervisor'
                  ? 'Supervisor view — execution, inspections, material, alerts'
                  : profile === 'manager'
                    ? 'Manager view — delivery, outsource, route sheets, analytics'
                    : 'Read-only plant overview'}
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Text type="secondary">Updated {lastUpdated.toLocaleTimeString()}</Text>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => setLastUpdated(new Date())}
            >
              Refresh
            </Button>
            <Switch
              checked={autoRefresh}
              onChange={setAutoRefresh}
              checkedChildren={<SyncOutlined spin />}
            />
            <Button icon={<DesktopOutlined />} onClick={toggleTv} type={tvMode ? 'primary' : 'default'}>
              TV mode
            </Button>
          </Space>
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 16 }} size="middle">
        <DatePicker.RangePicker
          value={dateRange}
          onChange={(v) => {
            const range = (v as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) || [null, null];
            setDateRange(range);
            syncUrlFilters({
              date_from: range[0]?.format('YYYY-MM-DD') || null,
              date_to: range[1]?.format('YYYY-MM-DD') || null,
            });
          }}
          format="DD/MM/YYYY"
          allowClear
        />
        <Input.Search
          placeholder="Search WO / customer / project"
          allowClear
          style={{ width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => {
            setSearch(v);
            syncUrlFilters({ search: v });
          }}
        />
        <Select
          allowClear
          placeholder="Execution scope"
          style={{ width: 180 }}
          value={executionScope}
          onChange={(v) => {
            setExecutionScope(v);
            syncUrlFilters({ execution_scope: v });
          }}
          options={[
            { value: 'Fabrication', label: 'Fabrication' },
            { value: 'Repair + Manufacturing', label: 'Repair + Manufacturing' },
            { value: 'Assembly', label: 'Assembly' },
          ]}
        />
        <Select
          allowClear
          placeholder="WO status"
          style={{ width: 160 }}
          value={workOrderStatus}
          onChange={(v) => {
            setWorkOrderStatus(v);
            syncUrlFilters({ work_order_status: v });
          }}
          options={[
            { value: 'Released', label: 'Released' },
            { value: 'In Progress', label: 'In Progress' },
            { value: 'Inspection Pending', label: 'Inspection Pending' },
            { value: 'On Hold', label: 'On Hold' },
          ]}
        />
        <Segmented
          value={kpiPeriod}
          onChange={(v) => setKPIPeriod(v as 'today' | 'week' | 'month')}
          options={[
            { label: 'Today', value: 'today' },
            { label: 'Week', value: 'week' },
            { label: 'Month', value: 'month' },
          ]}
        />
      </Space>

      <Tabs
        activeKey={towerTab}
        onChange={(k) => setTowerTab(k === 'analytics' ? 'analytics' : 'operations')}
        items={[
          {
            key: 'operations',
            label: 'Ops',
            children: opsContent,
          },
          {
            key: 'analytics',
            label: 'Analytics',
            children: visibility.analytics ? (
              <ManufacturingAnalytics period={kpiPeriod} filters={filters} />
            ) : (
              <Text type="secondary">Analytics not available for this layout.</Text>
            ),
          },
        ]}
      />
    </div>
  );
};

export default ManufacturingControlTowerPage;
